/**
 * The organization a user works in lives in the URL (`/o/{orgId}/...`), not on
 * the user: each tab can hold its own org and a link opens a specific org.
 * Pure helpers (no Angular, no Firebase) so the rules are unit-testable.
 *
 * Transition: `users/{uid}.currentOrganizationId` is still WRITTEN on an
 * explicit switch (other apps still read it) but is only a last-resort hint
 * here, for legacy URLs that carry no org.
 */

/** First path segment of org-scoped URLs. */
export const ORG_URL_SEGMENT = 'o';

/** `/o/{orgId}{path}`; `path` may carry a query string / fragment. */
export function orgUrl(orgId: string, path = '/home'): string {
  const rest = !path || path === '/' ? '/home' : path.startsWith('/') ? path : `/${path}`;
  return `/${ORG_URL_SEGMENT}/${encodeURIComponent(orgId)}${rest}`;
}

/** The org id of an `/o/{orgId}/...` URL, else null. */
export function orgIdFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const path = url.split(/[?#]/, 1)[0];
  const match = /^\/o\/([^/]+)(?:\/|$)/.exec(path);
  if (!match) return null;
  try {
    const id = decodeURIComponent(match[1]);
    return id && !id.includes('/') ? id : null;
  } catch {
    return null;
  }
}

/**
 * The org a URL without one resolves to: the org already active in this tab,
 * else the per-device last-used hint, else the legacy cross-app
 * `currentOrganizationId`, else the first org. Only orgs the user belongs to
 * (and that are reachable) qualify.
 */
export function pickDefaultOrgId(input: {
  activeOrgId: string | null;
  hintOrgId: string | null;
  remoteOrgId: string | null;
  memberOrgIds: readonly string[];
}): string | null {
  const { activeOrgId, hintOrgId, remoteOrgId, memberOrgIds } = input;
  const candidates = [activeOrgId, hintOrgId, remoteOrgId];
  return candidates.find((id): id is string => !!id && memberOrgIds.includes(id)) ?? memberOrgIds[0] ?? null;
}

export type UrlOrgDecision =
  /** The URL org is already the active org of this tab. */
  | 'active'
  /** A member org and nothing is active yet: activate it in place. */
  | 'activate'
  /**
   * A member org other than the active one: reload into it so every service,
   * signal and Firestore listener rehydrates (same reason the old switch reloaded).
   */
  | 'reload'
  /** One of the user's orgs, but its data region can't be reached. */
  | 'unavailable'
  /** Not one of the user's orgs: a clear "no access" state, never another org. */
  | 'no-access';

export function decideUrlOrg(input: {
  urlOrgId: string;
  activeOrgId: string | null;
  memberOrgIds: readonly string[];
  unavailableOrgIds: readonly string[];
}): UrlOrgDecision {
  const { urlOrgId, activeOrgId, memberOrgIds, unavailableOrgIds } = input;
  if (activeOrgId === urlOrgId) return 'active';
  if (memberOrgIds.includes(urlOrgId)) return activeOrgId ? 'reload' : 'activate';
  if (unavailableOrgIds.includes(urlOrgId)) return 'unavailable';
  return 'no-access';
}

/**
 * Where an impersonation session lands: the `next` path the partner console
 * asked for, but only inside the impersonated org (`/o/{impOrg}/...`), so a
 * crafted link can't send the session anywhere else; otherwise that org's home.
 */
export function impersonationLanding(next: string | null | undefined, impOrgId: string | null | undefined): string {
  if (!impOrgId) return '/';
  const home = orgUrl(impOrgId, '/home');
  if (!next || !next.startsWith('/') || next.startsWith('//')) return home;
  return orgIdFromUrl(next) === impOrgId ? next : home;
}
